// Globals
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';

// Models
import { IFilter } from '@/models/filters.model';

// Helpers
import { useGetFiltersList } from '../../hooks';

// Components
import { Form, FormItem, useWatchForm } from '@/components';
import { Button, Checkbox, Input } from '@/ui';
import { StyledCheckboxGroup } from '../../components/Styled';
import { CheckmarkIcon } from '@/icons';

type TFiltersFormProps = {
  form: FormInstance;
  initialFilters?: IFilter[];
  isLoading: boolean;
  onSubmit: (data: IFilter[]) => void;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
};

const FiltersForm: React.FC<TFiltersFormProps> = ({
  form,
  initialFilters,
  isLoading,
  onSubmit,
  setHasUnsavedChanges,
}) => {
  const { t } = useTranslation(['categories']);
  const { filtersList, isLoadingFiltersList } = useGetFiltersList();
  const selectedIds = (useWatchForm('selectedFilters', form) as IFilter[]) || [];
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSubmit = (values: { selectedFilters: IFilter[] }) => {
    onSubmit(values.selectedFilters);
  };

  const filteredList = useMemo(() => {
    const allFilters = filtersList?.data || [];
    if (!searchValue.trim()) return allFilters;
    const query = searchValue.toLowerCase().trim();
    return allFilters.filter((item) => item.locale?.uk?.title?.toLowerCase().includes(query));
  }, [filtersList?.data, searchValue]);

  const otherItems = filteredList.filter((item) => !selectedIds?.some((obj) => obj?.id === item.id));

  const checkboxes = useMemo(() => {
    return [...selectedIds, ...otherItems].map((item) => (
      <Checkbox key={item.id} value={item} onChange={(_) => setHasUnsavedChanges(true)}>
        {item.locale?.uk?.title}
      </Checkbox>
    ));
  }, [selectedIds, otherItems]);

  return (
    <Form
      disabled={isLoadingFiltersList}
      form={form}
      onFinish={handleSubmit}
      initialValues={{ selectedFilters: initialFilters }}
    >
      <Input placeholder={t('category_filters_search_input')} onChange={handleSearch} value={searchValue} />
      <div>
        <FormItem name="selectedFilters">
          <StyledCheckboxGroup>{checkboxes}</StyledCheckboxGroup>
        </FormItem>
      </div>
      <Button icon={<CheckmarkIcon />} loading={isLoading} type="primary" htmlType="submit" block>
        {t('category_filters_submit')}
      </Button>
    </Form>
  );
};

export default FiltersForm;
